import type { DriveProfile } from '../types/drive';
import { DRIVE_PROFILES } from '../config/profiles';
import { StateManager } from './StateManager';

export function calculateWriteValue(
  targetFreq: number,
  profile: DriveProfile,
): number {
  switch (profile.vendor) {
    case 'SIEMENS':
      return Math.round((targetFreq / 50.0) * 0x4000);
    case 'WEG':
      return Math.round(targetFreq * (120 / profile.poles));
    case 'ROCKWELL':
      // DSI/Modbus: Comm Frequency Command (reg 2001H) em unidades de 0.01 Hz
      return Math.round(targetFreq * 100);
    case 'DANFOSS':
    default:
      return Math.round(targetFreq * 10);
  }
}

export class PhysicsEngine {
  constructor(private state: StateManager) {}

  update(): void {
    const profileKey = this.state.get('selectedProfileKey');
    const profile = DRIVE_PROFILES[profileKey];
    if (!profile) return;
    const activeTrip = this.state.get('activeTrip');

    if (activeTrip) {
      this.handleTrippedState(profile);
    } else {
      this.handleNormalOperation(profile);
    }

    this.updateRegisters(profile);
    this.evaluateWarnings(profile);
  }

  private handleTrippedState(profile: DriveProfile): void {
    const currentFreq = this.state.get('currentFreq');
    this.state.set('currentFreq', Math.max(0, currentFreq - 2.5));
    this.state.set('currentAmp', 0);
    this.state.set('dcVoltage', profile.nominalDC - this.state.get('voltageDrop'));
  }

  private handleNormalOperation(profile: DriveProfile): void {
    const targetFreq = this.state.get('targetFreq');
    let currentFreq = this.state.get('currentFreq');

    if (currentFreq < targetFreq) {
      currentFreq = Math.min(targetFreq, currentFreq + 0.6);
    }
    if (currentFreq > targetFreq) {
      currentFreq = Math.max(targetFreq, currentFreq - 0.9);
    }
    this.state.set('currentFreq', currentFreq);

    const load = this.state.get('load');
    const imbalance = this.state.get('imbalance');
    const baseCurrent =
      (load / 100) * profile.nominalCurrent * (currentFreq / 50.0);
    const currentAmp =
      baseCurrent + imbalance * (profile.nominalCurrent * 0.015);
    this.state.set('currentAmp', currentAmp);

    const voltageDrop = this.state.get('voltageDrop');
    const nominalDC = profile.nominalDC - voltageDrop;
    const rippleFactor =
      (load / 100) * (this.state.get('looseTerminal') ? 45 : 4);
    const dcVoltage = nominalDC - Math.random() * rippleFactor;
    this.state.set('dcVoltage', dcVoltage);

    const thermalRatio = currentAmp / profile.nominalCurrent;
    const targetTemp =
      28.0 + thermalRatio * 35.0 + (this.state.get('cloggedHeatsink') ? 42.0 : 0.0);

    let heatsinkTemp = this.state.get('heatsinkTemp');
    if (heatsinkTemp < targetTemp) heatsinkTemp += 0.12;
    if (heatsinkTemp > targetTemp) heatsinkTemp -= 0.12;
    this.state.set('heatsinkTemp', heatsinkTemp);
  }

  private updateRegisters(profile: DriveProfile): void {
    const currentFreq = this.state.get('currentFreq');
    const currentAmp = this.state.get('currentAmp');
    const dcVoltage = this.state.get('dcVoltage');
    const heatsinkTemp = this.state.get('heatsinkTemp');

    const regs: Record<number, number> = {};

    regs[profile.regFreq] =
      profile.vendor === 'SIEMENS'
        ? Math.round((currentFreq / 50.0) * 0x4000)
        : profile.vendor === 'ROCKWELL'
          ? Math.round(currentFreq * 100) // DSI: 2103H em 0.01 Hz (xxx.xx Hz)
          : Math.round(currentFreq * 10);

    regs[profile.regCurrent] = Math.round(currentAmp * profile.currentScale);
    regs[profile.regDC] = Math.round(dcVoltage);
    regs[profile.regTemp] =
      profile.vendor === 'ROCKWELL' ? Math.round(heatsinkTemp) : Math.round(heatsinkTemp * 10);

    this.state.updateRegisters(regs);
  }

  private evaluateWarnings(profile: DriveProfile): void {
    const imbalance = this.state.get('imbalance');
    const dcVoltage = this.state.get('dcVoltage');
    const heatsinkTemp = this.state.get('heatsinkTemp');
    const fanMainHours = this.state.get('fanMainHours');
    const currentAmp = this.state.get('currentAmp');
    const activeTrip = this.state.get('activeTrip');

    this.state.setWarning('warn4', imbalance > 10);
    this.state.setWarning('warn7', dcVoltage > profile.nominalDC * 1.25);
    this.state.setWarning('warn8', dcVoltage < profile.nominalDC * 0.75);
    this.state.setWarning('warn13', heatsinkTemp > profile.maxTemp - 10);
    this.state.setWarning('warn25', fanMainHours > 45000);

    this.state.setAlarm(
      'alarm13',
      currentAmp > profile.nominalCurrent * 1.5 ||
        activeTrip === profile.tripCodes.OC.code,
    );

    this.state.setAlarm(
      'alarm7',
      dcVoltage > profile.nominalDC * 1.35 ||
        activeTrip === profile.tripCodes.OV.code,
    );

    this.state.setAlarm(
      'alarm8',
      dcVoltage < profile.nominalDC * 0.65 ||
        activeTrip === profile.tripCodes.UV.code,
    );

    this.state.setAlarm(
      'alarm29',
      heatsinkTemp >= profile.maxTemp ||
        activeTrip === profile.tripCodes.OT.code,
    );
  }
}
